// File: App.js
import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Outlet, useLocation } from "react-router-dom";
import pageConfig from "./config/pageConfig.js";
import guidesConfig from "./config/guidesConfig.js";
import GuidesMain from "./Components/GuidesMain/GuidesMain.jsx";
import ReactGA from "react-ga4";

import Home from "./Components/Home/Home.jsx";
import ScrollToTop from "./Components/ui/ScrollToTop.jsx";
import CalculatorMain from "./Components/GuidesMain/CalculatorMain.jsx";
import Faqs from "./Components/GuidesMain/Faqs.jsx";
import GuideLayout from "./Components/ui/GuideLayout.jsx";
import getStartedConfig from "./config/getStartedConfig.js";
import Privacy from "./Components/Compliance/Privacy.jsx";
import Disclaimer from "./Components/Compliance/Disclaimer.jsx";
import AffiliateDisclosure from "./Components/Compliance/AffiliateDisclosure.jsx";
import Sidebar from "./Components/Sidebar/Sidebar.js";
import Use from "./Components/Use/Use.jsx";
import Footer from "./Components/Footer/Footer.jsx";
import Nav from "./Components/Nav/Nav.jsx";
import NotFound from "./Components/ui/NotFound.jsx"; // 404 Component
import "./App.css";

// A helper component that renders an Outlet and a fallback footer if needed.
const CalculatorsRoutes = () => (
  <>
    {/* Index route for /calculators */}
    <Outlet />
    {/* If none of the nested calculator routes match, the wildcard route below will display <NotFound /> */}
  </>
);

const GuidesRoutes = () => (
  <>
    <Outlet />
  </>
);

const AppContent = () => {
  const location = useLocation();

  useEffect(() => {
    ReactGA.default.initialize('G-KHHRLKXRNN'); // replace with your GA4 Measurement ID
  }, []);

  // Track page views on route change
  useEffect(() => {
    ReactGA.default.send({ hitType: 'pageview', page: location.pathname + location.search });
  }, [location]);

  return (
    <div className="app-container">
      <Nav />
      <Sidebar />
      <main className="content">
        <ScrollToTop />
        <Routes>
          {/* Home */}
          <Route path="/" element={<Home />} />

          <Route path="/calculators" element={<CalculatorsRoutes />}>
    <Route index element={<CalculatorMain />} />
    {Object.values(pageConfig)
      .filter(page => page.component)
      .map(page => {
        const Component = page.component;
        const nestedPath = page.route.replace(/^\/calculators\//, "");
        return (
          <Route
            key={page.route}
            path={nestedPath}
            element={<Component {...page} />}
          />
        );
      })}
    <Route path="*" element={<NotFound />} />
  </Route>

  {/* Nested Guides Routes */}
  <Route path="/guides" element={<GuidesRoutes />}>
    {/* Index Route */}
    <Route
      index
      element={
          <GuidesMain />
      }
    />
    {Object.values(guidesConfig)
      .filter(guide => guide.component)
      .map(guide => {
        const Component = guide.component;
        const nestedPath = guide.route.replace(/^\/guides\//, "");
        return (
          <Route
            key={guide.route}
            path={nestedPath}
            element={
              <GuideLayout {...guide}>
                <Component {...guide} />
              </GuideLayout>
            }
          />
        );
      })}
    <Route path="*" element={<NotFound />} />
  </Route>

          {/* Get Started Pages */}
          {Object.values(getStartedConfig)
            .filter((page) => page.component)
            .map((page) => {
              const Component = page.component;
              return (
                <Route
                  key={page.route}
                  path={page.route}
                  element={<Component />}
                />
              );
            })}

          <Route path="/privacy-policy" element={<Privacy />} />
          <Route path="/faqs" element={<Faqs />} />

          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/affiliate-disclosure" element={<AffiliateDisclosure />} />

          {/* Global fallback for any other route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Footer props={{ size: "lg" }} />
      </main>
      {location.pathname.startsWith("/calculators/") ? <Use /> : null}
      <Footer props={{ size: "sm" }} />
    </div>
  );
};

function App() {
  useEffect(() => {
    const handleFocus = (event) => {
      if (event.target.tagName === "INPUT") {
        event.target.select();
      }
    };
    document.addEventListener("focus", handleFocus, true);
    return () => {
      document.removeEventListener("focus", handleFocus, true);
    };
  }, []);

  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
